import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { formatThemeMoney, planForMonths } from '@theme/utilities';

const DISPLAY_MONTHS = [3, 6, 1];

class ProductSupplySelector extends HTMLElement {
  #controller = null;
  #pricing = {};
  #otpVariants = [];
  #otpPricing = {};
  #purchaseType = 'subscription';
  #sellingPlanId = null;
  #selectedMonths = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#parsePricing();
    this.#parseOtpProduct();
    this.#buildOtpCards();

    this.addEventListener('change', this.#onVariantChange.bind(this), { signal });
    this.addEventListener('click', this.#onCardClick.bind(this), { signal });

    document.addEventListener(ThemeEvents.purchaseTypeUpdate, this.#onPurchaseTypeUpdate.bind(this), { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#onExternalVariantUpdate.bind(this), { signal });

    this.#syncPurchaseStateFromToggle();
    this.#applyActiveGroup();
    this.#updateCardAvailability();
    this.#updateAllPrices();
    this.#selectInitialVariant();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  getActiveVariant() {
    const useOtp = this.#usesOtpProduct();
    const group = this.#getGroup(useOtp ? 'otp' : 'subscription');
    const checked = group?.querySelector('.product-supply-selector__input:checked');
    const card = checked instanceof HTMLInputElement
      ? checked.closest('.product-supply-selector__card')
      : null;
    const available = checked instanceof HTMLInputElement && !checked.disabled;

    return {
      variantId: available ? this.#getCartVariantId(card, checked.value) : '',
      displayVariantId: available ? checked.value : '',
      available,
      months: Number(card?.getAttribute('data-months')) || 0,
      source: useOtp ? 'otp' : 'main',
      mainVariantId: card?.getAttribute('data-main-variant-id') || '',
    };
  }

  // Keep original variants for display, pricing and selling-plan selection.
  // Only the cart variant changes for GB/CA 3- and 6-month purchases.
  #getCartVariantId(card, fallbackVariantId) {
    const fallback = fallbackVariantId ? String(fallbackVariantId) : '';
    if (!(card instanceof Element)) return fallback;
    if (this.dataset.bundleRouting !== 'true') return fallback;
    if (!['GB', 'CA'].includes(this.dataset.marketCountry)) return fallback;

    const months = Number(card.getAttribute('data-months'));
    if (months !== 3 && months !== 6) return fallback;

    const purchase = this.#purchaseType === 'one_time' ? 'otp' : 'subscription';
    return this.getAttribute(`data-bundle-${purchase}-${months}`) || fallback;
  }

  #syncBundleFormVariant() {
    const active = this.getActiveVariant();
    if (active.available && active.variantId !== active.displayVariantId) {
      this.#syncFormVariant(active.variantId);
    }
  }

  #scheduleBundleFormSync() {
    this.#syncBundleFormVariant();
    // Reapply after other synchronous theme listeners have handled the event.
    queueMicrotask(() => {
      if (this.isConnected) this.#syncBundleFormVariant();
    });
  }

  getLowestAvailableOptionPrice() {
    const prices = [];
    const subscriptionCards = this.querySelectorAll(
      '[data-supply-group="subscription"] .product-supply-selector__card',
    );

    for (const card of subscriptionCards) {
      const variantId = card.getAttribute('data-variant-id');
      const pricing = variantId ? this.#pricing[String(variantId)] : null;

      if (!pricing || pricing.available === false) continue;

      const months = Number(card.getAttribute('data-months')) || Number(pricing.months) || 1;
      const plans = pricing.plans || {};

      if (Object.keys(plans).length > 0) {
        const subscriptionPrice = this.#getPriceForVariant(pricing, 'subscription', null);
        this.#addPerPackagePrice(prices, subscriptionPrice.price, months);
      }

      if (!this.#hasOtpProduct()) {
        const oneTimePrice = this.#getPriceForVariant(pricing, 'one_time', null);
        this.#addPerPackagePrice(prices, oneTimePrice.price, months);
      }
    }

    if (this.#hasOtpProduct()) {
      const otpCards = this.querySelectorAll(
        '[data-supply-group="otp"] .product-supply-selector__card',
      );

      for (const card of otpCards) {
        const variantId = card.getAttribute('data-variant-id');
        const otpVariant = variantId ? this.#otpPricing[String(variantId)] : null;

        if (!otpVariant || otpVariant.available === false) continue;

        const months = Number(card.getAttribute('data-months')) || Number(otpVariant.months) || 1;
        this.#addPerPackagePrice(prices, otpVariant.price, months);
      }
    }

    return prices.length > 0 ? Math.min(...prices) : null;
  }

  #parsePricing() {
    const dataEl = this.querySelector('[data-variant-pricing]');
    if (!dataEl?.textContent) return;

    try {
      this.#pricing = JSON.parse(dataEl.textContent);
    } catch {
      this.#pricing = {};
    }
  }

  #parseOtpProduct() {
    const productInfo = this.closest('[data-product-info]');
    const dataEl = productInfo?.querySelector('[data-otp-product]');
    if (!dataEl?.textContent) return;

    try {
      const parsed = JSON.parse(dataEl.textContent);
      this.#otpVariants = Array.isArray(parsed?.variants) ? parsed.variants : [];
    } catch {
      this.#otpVariants = [];
    }

    this.#otpPricing = {};
    for (const variant of this.#otpVariants) {
      this.#otpPricing[String(variant.id)] = variant;
    }
  }

  #getForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    return document.querySelector(`product-buy-buttons[data-product-id="${productId}"] form`);
  }

  #getGroup(name) {
    return this.querySelector(`[data-supply-group="${name}"]`);
  }

  #hasOtpProduct() {
    return this.#otpVariants.length > 0;
  }

  #usesOtpProduct() {
    return this.#purchaseType === 'one_time' && this.#hasOtpProduct();
  }

  #getVisibleGroup() {
    return this.#getGroup(this.#usesOtpProduct() ? 'otp' : 'subscription');
  }

  #syncPurchaseStateFromToggle() {
    const productId = this.dataset.productId;
    const toggle = document.querySelector(`product-purchase-toggle[data-product-id="${productId}"]`);
    if (!toggle) {
      this.#purchaseType = 'one_time';
      this.#sellingPlanId = null;
      return;
    }

    const checkedType = toggle.querySelector('[data-purchase-type]:checked');
    this.#purchaseType = checkedType?.value === 'one_time' ? 'one_time' : 'subscription';

    const planInput = toggle.querySelector('[data-plan-select]');
    this.#sellingPlanId = planInput instanceof HTMLInputElement ? planInput.value || null : null;
  }

  #buildOtpCards() {
    const otpGroup = this.#getGroup('otp');
    const subscriptionGroup = this.#getGroup('subscription');
    if (!otpGroup || !subscriptionGroup || !this.#hasOtpProduct()) return;

    otpGroup.replaceChildren();

    for (const months of DISPLAY_MONTHS) {
      const otpVariant = this.#otpVariants.find((variant) => Number(variant.months) === months);
      const templateCard = subscriptionGroup.querySelector(
        `.product-supply-selector__card[data-months="${months}"]`,
      );
      if (!otpVariant || !(templateCard instanceof HTMLElement)) continue;

      const card = templateCard.cloneNode(true);
      if (!(card instanceof HTMLElement)) continue;

      this.#hydrateOtpCard(card, otpVariant, templateCard);
      otpGroup.appendChild(card);
    }
  }

  #hydrateOtpCard(card, otpVariant, templateCard) {
    const mainVariantId = templateCard.getAttribute('data-main-variant-id') || '';
    const input = card.querySelector('.product-supply-selector__input');
    const label = card.querySelector('.product-supply-selector__select');
    const image = card.querySelector('.product-supply-selector__image');

    card.setAttribute('data-variant-id', String(otpVariant.id));
    card.setAttribute('data-main-variant-id', mainVariantId);
    card.classList.remove('product-supply-selector__card--selected');

    if (input instanceof HTMLInputElement) {
      const inputId = `${input.id}-otp`;
      input.id = inputId;
      input.value = String(otpVariant.id);
      input.name = `${input.name}-otp`;
      input.checked = false;
      input.disabled = otpVariant.available === false;
      input.setAttribute('data-variant-id', String(otpVariant.id));

      if (label instanceof HTMLLabelElement) {
        label.setAttribute('for', inputId);
      }
    }

    if (image instanceof HTMLImageElement) {
      if (otpVariant.image) {
        image.src = otpVariant.image;
        image.removeAttribute('srcset');
        image.alt = otpVariant.imageAlt || image.alt;
        image.hidden = false;
      } else {
        image.removeAttribute('src');
        image.removeAttribute('srcset');
        image.hidden = true;
      }
    }

    this.#updateOtpCardPrices(card, otpVariant);
  }

  #setGroupActive(groupName, isActive) {
    const group = this.#getGroup(groupName);
    if (!(group instanceof HTMLElement)) return;

    group.hidden = !isActive;
    group.setAttribute('aria-hidden', String(!isActive));

    for (const input of group.querySelectorAll('.product-supply-selector__input')) {
      if (!(input instanceof HTMLInputElement)) continue;

      if (!isActive) {
        input.disabled = true;
        continue;
      }

      const card = input.closest('.product-supply-selector__card');
      const variantId = card?.getAttribute('data-variant-id');
      const pricing = groupName === 'otp'
        ? this.#otpPricing[String(variantId)]
        : this.#pricing[String(variantId)];
      const available = pricing?.available !== false;
      const hasSubscriptionPlan = groupName === 'subscription'
        && Object.keys(pricing?.plans || {}).length > 0;
      input.disabled = !available
        || (groupName === 'subscription' && this.#purchaseType === 'subscription' && !hasSubscriptionPlan);
    }
  }

  #applyActiveGroup() {
    const useOtp = this.#usesOtpProduct();
    this.dataset.purchaseType = useOtp ? 'one_time' : 'subscription';
    this.#setGroupActive('subscription', !useOtp);
    this.#setGroupActive('otp', useOtp);
  }

  #getSelectedMonthsFromVisibleGroup() {
    const checked = this.#getVisibleGroup()?.querySelector('.product-supply-selector__input:checked');
    const card = checked instanceof HTMLInputElement
      ? checked.closest('.product-supply-selector__card')
      : null;
    const months = Number(card?.getAttribute('data-months'));

    return months > 0 ? months : this.#selectedMonths;
  }

  #getCardForMonths(groupName, months) {
    const group = this.#getGroup(groupName);
    if (!group || !months) return null;

    return group.querySelector(`.product-supply-selector__card[data-months="${months}"]`);
  }

  #getFirstAvailableCard(groupName) {
    const group = this.#getGroup(groupName);
    const input = group?.querySelector('.product-supply-selector__input:not(:disabled)');

    return input instanceof HTMLInputElement
      ? input.closest('.product-supply-selector__card')
      : null;
  }

  #updateCardAvailability() {
    const useOtp = this.#usesOtpProduct();
    this.#setGroupActive('subscription', !useOtp);
    this.#setGroupActive('otp', useOtp);
  }

  #syncFormVariant(variantId) {
    const form = this.#getForm();
    const variantInput = form?.querySelector('[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId ? String(variantId) : '';
    }
  }

  #selectInitialVariant() {
    const visibleGroup = this.#getVisibleGroup();
    const checked = visibleGroup?.querySelector('.product-supply-selector__input:checked');
    const hasValidCheckedInput = checked instanceof HTMLInputElement && !checked.disabled;
    const initialInput = hasValidCheckedInput
      ? checked
      : visibleGroup?.querySelector('.product-supply-selector__input:not(:disabled)');

    if (initialInput instanceof HTMLInputElement) {
      this.#selectVariant(initialInput.value, true);
      return;
    }

    if (this.#usesOtpProduct()) {
      this.#clearActiveSelection();
    }
  }

  #clearActiveSelection() {
    this.#syncFormVariant('');
    document.dispatchEvent(
      new VariantUpdateEvent(
        { id: '', available: false },
        this.dataset.blockId || '',
        { productId: this.dataset.productId || '' },
      ),
    );
  }

  #selectVariant(variantId, dispatchEvent = true) {
    const useOtp = this.#usesOtpProduct();
    const groupName = useOtp ? 'otp' : 'subscription';
    const card = this.#getGroup(groupName)?.querySelector(
      `.product-supply-selector__card[data-variant-id="${variantId}"]`,
    );
    if (!(card instanceof HTMLElement)) {
      if (useOtp) this.#clearActiveSelection();
      return;
    }

    const pricing = useOtp ? this.#otpPricing[String(variantId)] : this.#pricing[String(variantId)];
    if (!pricing) return;

    const group = this.#getGroup(groupName);
    for (const groupCard of group?.querySelectorAll('.product-supply-selector__card') || []) {
      groupCard.classList.toggle(
        'product-supply-selector__card--selected',
        groupCard.getAttribute('data-variant-id') === String(variantId),
      );
    }

    const input = card.querySelector('.product-supply-selector__input');
    if (input instanceof HTMLInputElement && !input.checked) {
      input.checked = true;
    }

    const available = pricing.available !== false && !(input instanceof HTMLInputElement && input.disabled);
    this.#selectedMonths = Number(card.getAttribute('data-months')) || 0;
    this.#syncFormVariant(available ? this.#getCartVariantId(card, variantId) : '');

    const mainVariantId = card.getAttribute('data-main-variant-id') || String(variantId);
    const toggle = document.querySelector(
      `product-purchase-toggle[data-product-id="${this.dataset.productId}"]`,
    );
    if (toggle && typeof toggle.syncFromVariant === 'function') {
      toggle.syncFromVariant(String(mainVariantId));
    }

    if (!dispatchEvent) {
      this.#scheduleBundleFormSync();
      return;
    }

    document.dispatchEvent(
      new VariantUpdateEvent(
        { id: available ? variantId : '', available },
        this.dataset.blockId || '',
        { productId: this.dataset.productId || '' },
      ),
    );
    this.#scheduleBundleFormSync();
  }

  #onVariantChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.classList.contains('product-supply-selector__input')) return;
    if (target.disabled) return;

    this.#selectVariant(target.value);
  }

  #onCardClick(event) {
    const card = event.target instanceof Element
      ? event.target.closest('.product-supply-selector__card')
      : null;
    if (!(card instanceof HTMLElement) || !this.contains(card)) return;
    if (event.target instanceof HTMLInputElement) return;

    const group = card.closest('[data-supply-group]');
    if (group instanceof HTMLElement && group.hidden) return;

    const variantId = card.getAttribute('data-variant-id');
    const input = card.querySelector('.product-supply-selector__input');
    if (!(input instanceof HTMLInputElement) || input.disabled || !variantId) return;

    input.checked = true;
    this.#selectVariant(variantId);
  }

  #onPurchaseTypeUpdate(event) {
    const productId = this.dataset.productId;
    const detail = event.detail;

    if (productId && detail?.productId && String(detail.productId) !== String(productId)) {
      return;
    }

    const previousType = this.#purchaseType;
    const months = this.#getSelectedMonthsFromVisibleGroup();
    this.#syncPurchaseStateFromToggle();

    if (previousType !== this.#purchaseType) {
      this.#selectedMonths = months || this.#selectedMonths;
      this.#applyActiveGroup();
      this.#restoreSelectionForActiveGroup();
    }

    this.#updateCardAvailability();
    this.#updateAllPrices();
    this.#scheduleBundleFormSync();
  }

  #restoreSelectionForActiveGroup() {
    const groupName = this.#usesOtpProduct() ? 'otp' : 'subscription';
    const matchedCard = this.#getCardForMonths(groupName, this.#selectedMonths);
    const matchedInput = matchedCard?.querySelector('.product-supply-selector__input');

    if (matchedInput instanceof HTMLInputElement && !matchedInput.disabled) {
      this.#selectVariant(matchedInput.value, true);
      return;
    }

    const fallbackCard = this.#getFirstAvailableCard(groupName);
    const fallbackInput = fallbackCard?.querySelector('.product-supply-selector__input');
    if (fallbackInput instanceof HTMLInputElement) {
      this.#selectVariant(fallbackInput.value, true);
      return;
    }

    this.#clearActiveSelection();
  }

  #onExternalVariantUpdate(event) {
    const sourceId = event.detail?.sourceId;
    if (sourceId === this.dataset.blockId) return;

    const variantId = event.detail?.resource?.id;
    if (!variantId) return;

    const input = this.querySelector(`.product-supply-selector__input[value="${variantId}"]`);
    if (!(input instanceof HTMLInputElement)) return;

    const card = input.closest('.product-supply-selector__card');
    const group = card?.closest('[data-supply-group]');
    if (group instanceof HTMLElement && group.hidden) return;

    input.checked = true;

    const visibleGroup = this.#getVisibleGroup();
    for (const groupCard of visibleGroup?.querySelectorAll('.product-supply-selector__card') || []) {
      const isSelected = groupCard.getAttribute('data-variant-id') === String(variantId);
      groupCard.classList.toggle('product-supply-selector__card--selected', isSelected);
    }

    this.#selectedMonths = Number(card?.getAttribute('data-months')) || this.#selectedMonths;
    this.#syncPurchaseStateFromToggle();
    this.#updateCardAvailability();
    this.#scheduleBundleFormSync();
  }

  #updateAllPrices() {
    for (const card of this.querySelectorAll('[data-supply-group="subscription"] .product-supply-selector__card')) {
      this.#updateCardBadge(card);

      const variantId = card.getAttribute('data-variant-id');
      if (!variantId) continue;

      this.#updateCardPrices(card, variantId);
    }

    for (const card of this.querySelectorAll('[data-supply-group="otp"] .product-supply-selector__card')) {
      this.#updateCardBadge(card);
      const variantId = card.getAttribute('data-variant-id');
      const otpVariant = variantId ? this.#otpPricing[String(variantId)] : null;
      if (!otpVariant) continue;

      this.#updateOtpCardPrices(card, otpVariant);
    }
  }

  #updateCardBadge(card) {
    const badge = card.querySelector('[data-badge-text]');
    if (!(badge instanceof HTMLElement)) return;

    const badgeText = this.#purchaseType === 'subscription'
      ? badge.dataset.subscriptionBadge || ''
      : badge.dataset.oneTimeBadge || '';

    badge.hidden = badgeText === '';
    badge.textContent = badgeText;
  }

  #updateCardPrices(card, variantId) {
    const pricing = this.#pricing[String(variantId)];
    if (!pricing) return;

    this.#renderCardPrices(card, this.#getPriceForVariant(pricing), pricing.months || 1);
  }

  #updateOtpCardPrices(card, otpVariant) {
    this.#renderCardPrices(
      card,
      {
        price: Number(otpVariant.price) || 0,
        compareAt: Number(otpVariant.compareAt) || 0,
      },
      Number(otpVariant.months) || 1,
    );
  }

  #renderCardPrices(card, { price, compareAt }, months) {
    const showTotal = this.dataset.priceDisplay === 'total';
    const duration = showTotal ? 1 : months > 0 ? months : 1;
    const perPackagePrice = Math.round(price / duration);
    const perPackageCompare = compareAt > 0 ? Math.round(compareAt / duration) : 0;
    const hasDiscount = perPackageCompare > perPackagePrice && perPackageCompare > 0;

    const priceEl = card.querySelector('[data-price]');
    if (priceEl) {
      priceEl.textContent = this.#formatMoney(perPackagePrice);
    }

    // Per-pack perk (full-price split-test block only). Always divides by the
    // real pack count, regardless of price display mode.
    const perPackEl = card.querySelector('[data-per-pack-text]');
    if (perPackEl instanceof HTMLElement) {
      const packs = months > 0 ? months : 1;
      const perPackPrice = Math.round((Number(price) || 0) / packs);
      const template = perPackEl.dataset.perPackTemplate || '[[price]] per pack';
      perPackEl.textContent = template.replace(/\[\[price\]\]/g, this.#formatMoney(perPackPrice));
    }

    const compareEl = card.querySelector('[data-compare-price]');
    const pillEl = card.querySelector('[data-percent-pill]');
    const percentEl = card.querySelector('[data-percent-text]');

    if (compareEl instanceof HTMLElement) {
      compareEl.hidden = !hasDiscount;
      if (hasDiscount) {
        compareEl.textContent = this.#formatMoney(perPackageCompare);
      }
    }

    if (pillEl instanceof HTMLElement) {
      pillEl.hidden = !hasDiscount;
    }

    if (percentEl && hasDiscount) {
      const percent = Math.floor(((perPackageCompare - perPackagePrice) / perPackageCompare) * 100);
      const template = this.dataset.percentOffTemplate || '__PERCENT__% off';
      percentEl.textContent = template.replace(/__PERCENT__|{{\s*percent\s*}}/g, String(percent));
    }
  }

  #getPriceForVariant(pricing, purchaseType = this.#purchaseType, sellingPlanId = this.#sellingPlanId) {
    const otpCompare = Number(pricing.otp?.compareAt) || 0;

    if (purchaseType === 'subscription') {
      const plans = pricing.plans || {};
      const planKeys = Object.keys(plans);
      if (planKeys.length > 0) {
        const months = pricing.months || 1;
        const durationPlan = planForMonths(Object.values(plans), months);
        const plan = durationPlan
          || (sellingPlanId && plans[sellingPlanId])
          || plans[planKeys[0]];
        const planCompare = Number(plan.compareAt) || 0;

        return {
          price: plan.price,
          compareAt: Math.max(planCompare, otpCompare),
        };
      }
    }

    return {
      price: pricing.otp.price,
      compareAt: otpCompare,
    };
  }

  #addPerPackagePrice(prices, price, months) {
    if (price === undefined || price === null) return;

    const numericPrice = Number(price);
    if (!Number.isFinite(numericPrice)) return;

    const duration = months > 0 ? months : 1;
    prices.push(Math.round(numericPrice / duration));
  }

  #formatMoney(cents) {
    return formatThemeMoney(cents, {
      moneyFormat: this.dataset.moneyFormat || '{{amount}}',
      moneyWithCurrencyFormat: this.dataset.moneyWithCurrencyFormat || '{{amount}}',
      currency: this.dataset.currency || 'USD',
    });
  }
}

if (!customElements.get('product-supply-selector')) {
  customElements.define('product-supply-selector', ProductSupplySelector);
}